import type { Document, PaginatedResponse } from "@/types/document";
import api from "@/api/api";
import cacheService from "./cache.service";

// Intercepteur pour ajouter le token aux requêtes
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

class DocumentService {
  async getAllDocuments(
    page: number = 1
  ): Promise<PaginatedResponse<Document>> {
    const cacheKey = `documents-page-${page}`;
    const cachedData = cacheService.get<PaginatedResponse<Document>>(cacheKey);
    if (cachedData) return cachedData;

    const response = await api.get(`/documents?page=${page}`);
    const result = response.data.documents;
    cacheService.set(cacheKey, result, 30); // Cache pour 30 minutes
    return result;
  }

  async getDocument(id: string): Promise<Document> {
    const cacheKey = `document-${id}`;
    const cachedData = cacheService.get<Document>(cacheKey);
    if (cachedData) return cachedData;

    const response = await api.get(`/documents/${id}`);
    const result = response.data;
    cacheService.set(cacheKey, result, 30);
    return result;
  }

  async createDocument(document: Partial<Document>): Promise<Document> {
    const response = await api.post("/documents", document);
    const result = response.data;
    // Invalider le cache des listes
    cacheService.remove("documents-page-1");
    return result;
  }

  async updateDocument(
    id: string,
    document: Partial<Document>
  ): Promise<Document> {
    const response = await api.put(`/documents/${id}`, document);
    const result = response.data;
    // Invalider le cache du document et des listes
    cacheService.remove(`document-${id}`);
    cacheService.remove("documents-page-1");
    return result;
  }

  async deleteDocument(id: string): Promise<void> {
    await api.delete(`/documents/${id}`);
    // Invalider le cache du document et des listes
    cacheService.remove(`document-${id}`);
    cacheService.remove("documents-page-1");
  }

  async uploadDocument(file: File): Promise<Document> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post("/documents/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    const result = response.data;
    // Invalider le cache des listes
    cacheService.remove("documents-page-1");
    return result;
  }
}

export default new DocumentService();
